import { useState } from 'react';
import {
  Box,
  IconButton,
  Badge,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  VStack,
  HStack,
  Text,
  Divider,
  useColorModeValue,
  useDisclosure,
  Portal,
} from '@chakra-ui/react';
import { BellIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetchNotifications, apiMarkNotificationRead } from 'api';
import { NotificationDetailModal } from './NotificationDetailModal';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import type { Notification, APIPaginatedResponse } from 'types/api.types';

export const NotificationBell = () => {
  const isNotificationsEnabled = useFeatureFlag('enable-big-communications');
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const unreadBg = useColorModeValue('blue.50', 'blue.900');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // Fetch all notifications with pagination
  const { data: allNotificationsData } = useQuery({
    queryKey: ['notifications', 'all', page],
    queryFn: () => apiFetchNotifications(page, 'all', 10),
    refetchInterval: 300000, // Refetch every 5 minutes
  }) as { data: APIPaginatedResponse<Notification> };

  const notifications: Notification[] = allNotificationsData?.results || [];
  const totalCount = allNotificationsData?.count || 0;
  const hasNext = !!allNotificationsData?.next;
  const hasPrevious = !!allNotificationsData?.previous;
  const unreadCount = notifications.filter((n: Notification) => n.status === 'unread').length;

  // Mutation to mark notification as read
  const markReadMutation = useMutation({
    mutationFn: apiMarkNotificationRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Don't render if feature flag is disabled
  if (!isNotificationsEnabled) {
    return null;
  }

  const handleNotificationClick = (notification: Notification) => {
    setSelectedNotification(notification);
    
    // For non-acknowledgement notifications that are unread, automatically mark as read
    if (!notification.requires_acknowledgement && notification.status === 'unread') {
      markReadMutation.mutate(notification.id);
    }
    
    onModalOpen();
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <>
      <Popover placement="bottom-end" closeOnBlur={true}>
        <PopoverTrigger>
          <IconButton
            icon={
              <Box position="relative">
                <BellIcon w={5} h={5} />
                {unreadCount > 0 && (
                  <Badge
                    position="absolute"
                    top="-8px"
                    right="-8px"
                    colorScheme="red"
                    borderRadius="full"
                    fontSize="xs"
                    minW="20px"
                    h="20px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {unreadCount}
                  </Badge>
                )}
              </Box>
            }
            variant="ghost"
            aria-label="Notifications"
          />
        </PopoverTrigger>
        
        <Portal>
          <PopoverContent
            bg={bgColor}
            borderColor={borderColor}
            w="400px"
            maxH="600px"
            boxShadow="xl"
            zIndex={10000}
          >
          <PopoverHeader fontWeight="bold" borderBottomWidth="1px">
            <HStack justify="space-between">
              <Text>Notifications</Text>
              {unreadCount > 0 && (
                <Badge colorScheme="blue">{unreadCount} unread</Badge>
              )}
            </HStack>
          </PopoverHeader>
          
          <PopoverBody p={0} maxH="450px" overflowY="auto">
            {notifications.length === 0 ? (
              <Box p={4} textAlign="center">
                <Text color="gray.500">No notifications</Text>
              </Box>
            ) : (
              <VStack spacing={0} align="stretch">
                {notifications.map((notification: Notification, index: number) => (
                  <Box key={notification.id}>
                    <Box
                      p={4}
                      cursor="pointer"
                      bg={notification.status === 'unread' ? unreadBg : 'transparent'}
                      _hover={{ bg: hoverBg }}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <VStack align="start" spacing={1}>
                        <HStack w="full" justify="space-between">
                          <Text
                            fontSize="sm"
                            fontWeight={notification.status === 'unread' ? 'bold' : 'normal'}
                            noOfLines={1}
                            flex={1}
                          >
                            {notification.title}
                          </Text>
                          <HStack spacing={1}>
                            {notification.is_edited && (
                              <Badge colorScheme="blue" fontSize="xs">
                                Edited
                              </Badge>
                            )}
                            {notification.requires_acknowledgement && (
                              <Badge colorScheme="red" fontSize="xs">
                                ⚠️
                              </Badge>
                            )}
                          </HStack>
                        </HStack>
                        
                        <Text
                          fontSize="xs"
                          color="gray.600"
                          noOfLines={2}
                          fontWeight={notification.status === 'unread' ? 'semibold' : 'normal'}
                        >
                          {notification.summary || 'No summary'}
                        </Text>
                        
                        <Text fontSize="xs" color="gray.400">
                          {formatTimeAgo(notification.created_at)}
                        </Text>
                      </VStack>
                    </Box>
                    {index < notifications.length - 1 && <Divider />}
                  </Box>
                ))}
              </VStack>
            )}
          </PopoverBody>

          {totalCount > 0 && (
            <PopoverFooter borderTopWidth="1px">
              <HStack justify="space-between" align="center">
                <Text fontSize="xs" color="gray.500">
                  Showing {notifications.length} of {totalCount}
                </Text>
                <HStack spacing={2}>
                  <IconButton
                    size="xs"
                    icon={<ChevronLeftIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPage(p => Math.max(1, p - 1));
                    }}
                    isDisabled={!hasPrevious}
                    aria-label="Previous page"
                  />
                  <Text fontSize="xs">{page}</Text>
                  <IconButton
                    size="xs"
                    icon={<ChevronRightIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPage(p => p + 1);
                    }}
                    isDisabled={!hasNext}
                    aria-label="Next page"
                  />
                </HStack>
              </HStack>
            </PopoverFooter>
          )}
          </PopoverContent>
        </Portal>
      </Popover>

      {/* Modal for non-mandatory notifications */}
      <NotificationDetailModal
        isOpen={isModalOpen}
        onClose={onModalClose}
        notification={selectedNotification}
      />
    </>
  );
};
